/**
 * Dalmaydi storefront behaviours
 * Mobile nav, scroll reveal, header scroll state
 * Product grids, galleries, and forms are handled by Shopify / page templates.
 */
(function () {
  'use strict';

  function initNav() {
    const toggle = document.querySelector('[data-nav-toggle]');
    const mobileNav = document.querySelector('[data-mobile-nav]');
    if (!toggle || !mobileNav) return;

    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      mobileNav.classList.toggle('is-open', !open);
    });

    mobileNav.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => {
        toggle.setAttribute('aria-expanded', 'false');
        mobileNav.classList.remove('is-open');
      });
    });
  }

  function initReveal() {
    const items = document.querySelectorAll('.reveal');
    if (!items.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const parents = new Set();
    items.forEach((el) => {
      if (el.parentElement) parents.add(el.parentElement);
    });
    parents.forEach((parent) => {
      const kids = [...parent.children].filter((c) => c.classList.contains('reveal'));
      kids.forEach((kid, i) => {
        if (!kid.style.getPropertyValue('--reveal-delay')) {
          kid.style.setProperty('--reveal-delay', `${Math.min(i * 90, 360)}ms`);
        }
      });
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -48px 0px' }
    );

    items.forEach((el) => observer.observe(el));
  }

  function initHeaderScroll() {
    const header = document.querySelector('.site-header');
    if (!header) return;

    const onScroll = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 24);
    };

    const scroller = document.querySelector('.page-wrapper');
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    if (scroller && scroller !== document.documentElement) {
      scroller.addEventListener(
        'scroll',
        () => {
          header.classList.toggle('is-scrolled', scroller.scrollTop > 24);
        },
        { passive: true }
      );
    }
  }

  function initFacets() {
    const tagMatch = window.location.pathname.match(/\/(carterii|frereana|myrrh)\/?$/i);
    if (tagMatch) {
      const path = window.location.pathname.replace(/\/(carterii|frereana|myrrh)\/?$/i, '');
      const params = new URLSearchParams(window.location.search);
      params.set('botanical', tagMatch[1].toLowerCase());
      window.location.replace(path + '?' + params.toString());
      return;
    }

    const form = document.querySelector('[data-facets]');
    const drawer = document.querySelector('[data-facets-drawer]');
    const overlay = document.querySelector('[data-facets-overlay]');
    const openBtn = document.querySelector('[data-facets-open]');
    const closeBtn = document.querySelector('[data-facets-close]');
    if (!form && !drawer) return;

    const applyBtn = form && form.querySelector('[data-facets-apply]');
    if (applyBtn) applyBtn.hidden = true;

    if (form) {
      form.addEventListener('change', (event) => {
        if (event.target.matches('input, select')) form.submit();
      });
    }

    const params = new URLSearchParams(window.location.search);
    const selected = params
      .getAll('botanical')
      .join(',')
      .split(',')
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);
    const grid = document.querySelector('.product-grid--collection');
    if (grid && selected.length) {
      const cards = [...grid.querySelectorAll('[data-botanical]')];
      let visible = 0;
      cards.forEach((card) => {
        const match = selected.includes((card.getAttribute('data-botanical') || '').toLowerCase());
        card.hidden = !match;
        if (match) visible += 1;
      });
      const countEl = document.querySelector('.collection-toolbar__count');
      if (countEl) {
        countEl.textContent = `${visible} product${visible === 1 ? '' : 's'}`;
      }
      const empty = document.querySelector('.collection-empty');
      if (grid) grid.hidden = visible === 0;
      if (empty) empty.hidden = visible !== 0;
    }

    function setDrawer(open) {
      if (!drawer) return;
      drawer.classList.toggle('is-open', open);
      if (overlay) overlay.classList.toggle('is-open', open);
      document.body.classList.toggle('facets-open', open);
      if (openBtn) openBtn.setAttribute('aria-expanded', String(open));
    }

    if (openBtn) {
      openBtn.addEventListener('click', () => setDrawer(true));
    }
    if (closeBtn) {
      closeBtn.addEventListener('click', () => setDrawer(false));
    }
    if (overlay) {
      overlay.addEventListener('click', () => setDrawer(false));
    }
    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') setDrawer(false);
    });
  }

  function fieldValue(form, name) {
    const field = form.querySelector(`[name="${name}"]`);
    return field ? String(field.value || '').trim() : '';
  }

  function initQuoteForm() {
    document.querySelectorAll('.dalmaydi-quote-form').forEach((form) => {
      form.addEventListener('submit', () => {
        const notes = fieldValue(form, 'contact[body]');
        const lines = [
          'Wholesale quotation request',
          `Product: ${fieldValue(form, 'contact[product]') || '—'}`,
          `Quantity: ${fieldValue(form, 'contact[quantity]') || '—'}`,
          `Company: ${fieldValue(form, 'contact[company]') || '—'}`,
          `Country: ${fieldValue(form, 'contact[country]') || '—'}`,
          `Phone: ${fieldValue(form, 'contact[phone]') || '—'}`,
          '',
          'Message:',
          notes || '—',
        ];
        const body = form.querySelector('[name="contact[body]"]');
        if (body) body.value = lines.join('\n');
      });
    });

    const success = document.querySelector('.dalmaydi-quote-form .form-status--success, .dalmaydi-contact-form .form-status--success');
    const quoteSection = document.getElementById('get-quotation');
    if (success && quoteSection) {
      quoteSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (success) {
      success.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    document.querySelectorAll('.dalmaydi-contact-form').forEach((form) => {
      form.addEventListener('submit', () => {
        const company = fieldValue(form, 'contact[company]');
        const body = form.querySelector('[name="contact[body]"]');
        if (!body) return;
        const notes = String(body.value || '').trim();
        if (company) {
          body.value = ['Company: ' + company, '', notes].join('\n');
        }
      });
    });

    document.querySelectorAll('[data-form-status]').forEach((status) => {
      if (status.dataset.bound === 'true') return;
      status.dataset.bound = 'true';

      const hide = () => {
        status.classList.remove('is-visible');
        status.hidden = true;
      };

      const close = status.querySelector('[data-form-status-close]');
      if (close) close.addEventListener('click', hide);

      const timeout = Number(status.getAttribute('data-form-status-timeout'));
      if (timeout > 0) window.setTimeout(hide, timeout);
    });
  }

  function initGallery() {
    const main = document.querySelector('[data-gallery-main]');
    const thumbs = document.querySelectorAll('[data-gallery-thumb]');
    if (!main || !thumbs.length) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.getAttribute('data-src');
        const alt = thumb.getAttribute('data-alt') || '';
        if (!src) return;
        main.src = src;
        main.alt = alt;
        thumbs.forEach((item) => item.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initNav();
    initHeaderScroll();
    initReveal();
    initFacets();
    initGallery();
    initQuoteForm();
  });

  document.addEventListener('shopify:section:load', () => {
    initNav();
    initHeaderScroll();
    initReveal();
    initFacets();
    initGallery();
    initQuoteForm();
  });
})();
